import { capitalize, startCase } from 'lodash';
import type { ReactNode } from 'react';

import type {
  AtlasGqlPictureInferenceFilterInput,
  AtlasGqlPictureInferenceFilterOption,
  AtlasGqlPictureInferenceRangeFilterOption,
} from 'types/atlas-graphql';

// NOTE (sc-163520): this consumes the new `pvPanelDetection` picture inference
// filter (label + risk), replacing the deprecated `panelDetectionMeta`
// (detectionTypes). observation-service publishes the filter schema to the
// Atlas variant, and `types/atlas-graphql` is generated against it (codegen).

type DetectionType = AtlasGqlPictureInferenceFilterOption;
type RangeFilter = AtlasGqlPictureInferenceRangeFilterOption;

// The pv panel detection filter exposes two selectable dimensions (label and
// risk) plus a deltaT range. The picker keeps one flat list of selected values,
// so each option value is namespaced with its filter field: `${field}::${id}`.
export type PictureInferenceFilterField = 'labels' | 'risks' | 'bladeDamageTypes';

const FILTER_VALUE_SEPARATOR = '::';

export const encodePictureInferenceFilterValue = (
  field: PictureInferenceFilterField,
  id: string
): string => `${field}${FILTER_VALUE_SEPARATOR}${id}`;

export const decodePictureInferenceFilterValue = (
  value: string
): { field: PictureInferenceFilterField; id: string } | undefined => {
  const separatorIndex = value.indexOf(FILTER_VALUE_SEPARATOR);
  if (separatorIndex === -1) {
    return undefined;
  }

  const field = value.slice(0, separatorIndex) as PictureInferenceFilterField;
  const id = value.slice(separatorIndex + FILTER_VALUE_SEPARATOR.length);

  if ((field !== 'labels' && field !== 'risks' && field !== 'bladeDamageTypes') || !id) {
    return undefined;
  }

  return { field, id };
};

type FilterOptionsGroupValue = {
  confidenceRange?: RangeFilter | null;
  damageTypes?: Array<DetectionType | null> | null;
  deltaTRange?: RangeFilter | null;
  inferenceApplication?: string | null;
  labels?: Array<DetectionType | null> | null;
  risks?: Array<DetectionType | null> | null;
} | null;

const pictureInferenceFilterOptionGroupKeys = [
  'pvPanelDetection',
  'bladeInternalDamageDetection',
] as const;

type PictureInferenceFilterOptionGroupKey = (typeof pictureInferenceFilterOptionGroupKeys)[number];

export type PictureInferenceFilterOptionsValue = Partial<
  Record<PictureInferenceFilterOptionGroupKey, FilterOptionsGroupValue>
>;

// Labels for which a deltaT threshold is meaningless: "none" means the inference
// ran but found no anomalous panel, so there is nothing to measure a delta on.
export const DELTA_T_INCOMPATIBLE_LABEL_IDS = ['none'];

export const hasDeltaTIncompatibleDetectionType = (
  selectedValues: string[],
  field: PictureInferenceFilterField
) =>
  selectedValues.some((value) => {
    const decoded = decodePictureInferenceFilterValue(value);
    return decoded?.field === field && DELTA_T_INCOMPATIBLE_LABEL_IDS.includes(decoded.id);
  });

export const formatInferenceApplicationLabel = (value: string) => {
  const normalizedValue = startCase(value).trim().toLowerCase();

  if (!normalizedValue) {
    return value;
  }

  return capitalize(normalizedValue);
};

export type PictureInferenceDetectionTypeOption = {
  description?: string;
  label: string;
  value: string;
};

export type PictureInferenceDetectionTypeSelectOption = Omit<
  PictureInferenceDetectionTypeOption,
  'label'
> & {
  label: ReactNode;
  searchText: string;
};

export type PictureInferenceFilterOptionGroup = {
  confidenceRange?: RangeFilter;
  deltaTRange?: RangeFilter;
  field: PictureInferenceFilterField;
  inferenceApplication: string;
  options: PictureInferenceDetectionTypeOption[];
};

export type PictureInferenceFilterSelectOptionGroup = {
  label: string;
  options: PictureInferenceDetectionTypeSelectOption[];
};

// Each pv panel detection group renders as two selectable dimensions, each shown
// as its own labelled group in the picker.
const PICTURE_INFERENCE_FILTER_DIMENSIONS: Array<{
  field: PictureInferenceFilterField;
  labelKey: string;
  label: string;
}> = [
  { field: 'labels', labelKey: 'inferenceFilter.anomalyLabelHeader', label: 'Anomaly label' },
  { field: 'risks', labelKey: 'inferenceFilter.riskHeader', label: 'Risk' },
];

// The dimension headers are the UI's own copy, so they are localized; option
// and range labels arrive from observation-service and are rendered verbatim —
// dynamic API values are deliberately NOT translated. The one exception is the
// "Confidence" slider title: it is a plain English word (unlike "Delta T",
// which reads as a technical term in every locale), so it is localized by its
// stable range id. Matches i18next's t(key, defaultValue) signature.
export type PictureInferenceFilterTranslate = (key: string, defaultValue: string) => string;

const identityTranslate: PictureInferenceFilterTranslate = (_key, defaultValue) => defaultValue;

const localizeConfidenceRange = (
  translate: PictureInferenceFilterTranslate,
  range?: RangeFilter | null
): RangeFilter | undefined =>
  range
    ? { ...range, label: translate('inferenceFilter.confidenceLabel', range.label) }
    : undefined;

export const buildPictureInferenceDetectionTypeOptions = (
  field: PictureInferenceFilterField,
  options: Array<DetectionType | null> = []
): PictureInferenceDetectionTypeOption[] => {
  return [...options]
    .filter((option): option is DetectionType => Boolean(option?.id && option?.label))
    .map((option) => ({
      description: option.description ?? undefined,
      label: option.label,
      value: encodePictureInferenceFilterValue(field, option.id),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
    );
};

export const filterSelectedPictureInferenceDetectionTypeIds = (
  selectedDetectionTypeIds: string[] = [],
  availableDetectionTypeIds?: string[]
): string[] => {
  if (!selectedDetectionTypeIds.length) {
    return selectedDetectionTypeIds;
  }

  if (!availableDetectionTypeIds) {
    return selectedDetectionTypeIds;
  }

  const availableIds = new Set(availableDetectionTypeIds);

  return selectedDetectionTypeIds.filter((id) => availableIds.has(id));
};

const buildPictureInferenceFilterOptionGroupsForKey = (
  group: FilterOptionsGroupValue,
  translate: PictureInferenceFilterTranslate
): PictureInferenceFilterOptionGroup[] => {
  if (!group) {
    return [];
  }

  const groups: PictureInferenceFilterOptionGroup[] = [];
  let rangesAttached = false;

  PICTURE_INFERENCE_FILTER_DIMENSIONS.forEach(({ field, labelKey, label }) => {
    const options = buildPictureInferenceDetectionTypeOptions(field, group[field] ?? []);

    if (!options.length) {
      return;
    }

    groups.push({
      // Attach the shared deltaT + confidence ranges to the first non-empty
      // dimension only, so each slider is rendered once per group.
      deltaTRange: rangesAttached ? undefined : (group.deltaTRange ?? undefined),
      confidenceRange: rangesAttached
        ? undefined
        : localizeConfidenceRange(translate, group.confidenceRange),
      field,
      inferenceApplication: translate(labelKey, label),
      options,
    });

    if (group.deltaTRange || group.confidenceRange) {
      rangesAttached = true;
    }
  });

  return groups;
};

export const buildPictureInferenceFilterOptionGroups = (
  pictureInferenceFilterOptions?: PictureInferenceFilterOptionsValue | null,
  translate: PictureInferenceFilterTranslate = identityTranslate
): PictureInferenceFilterOptionGroup[] => {
  if (!pictureInferenceFilterOptions) {
    return [];
  }

  return pictureInferenceFilterOptionGroupKeys.flatMap((groupKey) => {
    const group = pictureInferenceFilterOptions[groupKey];
    if (groupKey === 'bladeInternalDamageDetection') {
      if (!group?.damageTypes) {
        return [];
      }
      return [
        {
          confidenceRange: localizeConfidenceRange(translate, group.confidenceRange),
          field: 'bladeDamageTypes',
          inferenceApplication: translate(
            'imageViewer.internalDamageDetection',
            'Internal Damage detection'
          ),
          options: buildPictureInferenceDetectionTypeOptions('bladeDamageTypes', group.damageTypes),
        },
      ];
    }
    return buildPictureInferenceFilterOptionGroupsForKey(group, translate);
  });
};

export const buildPictureInferenceFilterSelectOptions = (
  pictureInferenceFilterOptions?: PictureInferenceFilterOptionsValue | null,
  translate: PictureInferenceFilterTranslate = identityTranslate
): PictureInferenceFilterSelectOptionGroup[] => {
  const groups = buildPictureInferenceFilterOptionGroups(pictureInferenceFilterOptions, translate);

  if (!groups.length) {
    return [];
  }

  return groups.map((group) => ({
    label: group.inferenceApplication,
    options: group.options.map((option) => ({
      ...option,
      searchText: option.label,
    })),
  }));
};

export const buildPictureInferenceFilterInput = (
  selectedValues: string[] = [],
  deltaTMin?: number,
  confidenceMin?: number,
  inferenceApplication: 'pvPanelDetection' | 'bladeInternalDamageDetection' = 'pvPanelDetection'
): AtlasGqlPictureInferenceFilterInput | undefined => {
  const labels: string[] = [];
  const risks: string[] = [];
  const damageTypes: string[] = [];

  selectedValues.forEach((value) => {
    const decoded = decodePictureInferenceFilterValue(value);

    if (!decoded) {
      return;
    }

    if (inferenceApplication === 'pvPanelDetection' && decoded.field === 'labels') {
      labels.push(decoded.id);
    } else if (inferenceApplication === 'pvPanelDetection' && decoded.field === 'risks') {
      risks.push(decoded.id);
    } else if (
      inferenceApplication === 'bladeInternalDamageDetection' &&
      decoded.field === 'bladeDamageTypes'
    ) {
      damageTypes.push(decoded.id);
    }
  });

  // Row-level ranges (deltaT, confidence) only apply once at least one concrete
  // detection could match — mirror the "none disables ranges" server behaviour.
  const rangeFiltersApplicable = !labels.includes('none');
  const normalizedDeltaTMin =
    rangeFiltersApplicable &&
    inferenceApplication === 'pvPanelDetection' &&
    Number.isFinite(deltaTMin)
      ? deltaTMin
      : undefined;
  const normalizedConfidenceMin =
    rangeFiltersApplicable &&
    inferenceApplication === 'pvPanelDetection' &&
    Number.isFinite(confidenceMin)
      ? confidenceMin
      : undefined;
  if (
    !labels.length &&
    !risks.length &&
    !damageTypes.length &&
    normalizedDeltaTMin === undefined &&
    normalizedConfidenceMin === undefined
  ) {
    return undefined;
  }

  return {
    ...(damageTypes.length
      ? {
          bladeInternalDamageDetection: {
            damageTypes,
          },
        }
      : {}),
    ...(!labels.length &&
    !risks.length &&
    normalizedDeltaTMin === undefined &&
    normalizedConfidenceMin === undefined
      ? {}
      : {
          pvPanelDetection: {
            ...(labels.length ? { labels } : {}),
            ...(risks.length ? { risks } : {}),
            ...(normalizedDeltaTMin === undefined
              ? {}
              : { deltaTRange: { min: normalizedDeltaTMin } }),
            ...(normalizedConfidenceMin === undefined
              ? {}
              : { confidenceRange: { min: normalizedConfidenceMin } }),
          },
        }),
  };
};
